import React, { useContext, useState, useEffect, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageContext } from '../../context/PageContext';
import { Helmet } from 'react-helmet-async';
import { ThemeContext } from '../../context/ThemeContext';
import { formatExtra } from '../../constants/categorias';
import { API_URL, buildBackendImgUrl } from '../../utils/api';
import { getContrastColor as getContrast } from '../../utils/colorUtils';
import AdaptiveGrid from '../../components/ImageGrid/AdaptiveGrid';
import DetailGrid from '../../components/ImageGrid/DetailGrid';
import {
  BuscadorTop, ContainerBuscador, FiltroSep, SearchBox, SearchButton, VistaBtn,
} from '../../components/Formularios/Buscador/Buscador.styles';
import {
  PageWrapper, Grid,
  CardWrapper, CardInfo, CardPrice, CardNombre, CardDesc, CardBadges, CardBadge, ChatIconBtn,
  Overlay, DetailBox, DetailScroll, DetailClose, DetailTitle, DetailPrice, DetailMeta, MetaTag, DetailDesc,
  ShareRow, ShareLabel, ShareBtn, LbOverlay, LbImg, LbNav, LbClose, LbDots, LbDot,
  ToggleBtn, FlatGrid, FlatCard, FlatImg, EmptyState, VendidoRibbon,
} from './De.styles';

const ModalChat = React.lazy(() => import('../../components/Modal/ModalChat/ModalChat'));

// #trasterocard — tarjeta pública (título+💬 arriba, AdaptiveGrid, CardInfo)
function TrasteroCard({ task, theme, onOpen, onChat }) {
  const bg  = theme.modalBg || '#1a1a1a';
  const acc = theme.accent  || '#667eea';
  const txt = getContrast(bg);
  const thumbs = [task.Thumb1||task.Imagen1, task.Thumb2||task.Imagen2, task.Thumb3||task.Imagen3, task.Thumb4||task.Imagen4];
  const imgs   = [task.Imagen1, task.Imagen2, task.Imagen3, task.Imagen4];
  const precio = task.Precio !== null && task.Precio !== undefined
    ? task.Precio.toLocaleString('es-ES', { minimumFractionDigits: 0 }) + ' €'
    : null;
  const extra = formatExtra(task.Extras);

  return (
    <CardWrapper bg={bg} onClick={() => onOpen(task)}>
      {/* 🔴 Ribbon de VENDIDO */}
      {task.vendido && <VendidoRibbon>VENDIDO</VendidoRibbon>}

      <div style={{
        background: theme.cardTitle || '#1a1a2e',
        color: getContrast(theme.cardTitle || '#1a1a2e'),
        padding: '0 12px', height: 54,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <span style={{ flex: 1, fontSize: 15, fontWeight: 700, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {task.Nombre}
        </span>
        <ChatIconBtn onClick={e => { e.stopPropagation(); onChat(task); }}>💬</ChatIconBtn>
      </div>

      <div style={{ position: 'relative', overflow: 'hidden' }}>
        <AdaptiveGrid ruta={buildBackendImgUrl(task.Ruta)} imgs={imgs} thumbs={thumbs} width="100%" />
      </div>

      <CardInfo>
        {precio && <CardPrice accent={acc}>{precio}</CardPrice>}
        {task.Descripcion && <CardDesc color={txt}>{task.Descripcion}</CardDesc>}
        {extra && <div style={{ fontSize: 12, color: txt, opacity: 0.75 }}>{extra}</div>}
        {(task.AceptaCambio || task.Negociable) && (
          <CardBadges>
            {task.AceptaCambio && <CardBadge accent={acc}>Acepta cambio</CardBadge>}
            {task.Negociable   && <CardBadge accent={acc}>Negociable</CardBadge>}
          </CardBadges>
        )}
      </CardInfo>
    </CardWrapper>
  );
}

// #component — estado, fetch, lightbox, modal detalle, ModalChat
export default function De({ user }) {
  const { nombre }       = useParams();
  const navigate         = useNavigate();
  const { theme }        = useContext(ThemeContext);
  const { setBadge, setLogo } = usePageContext();

  const [arts,         setArts]         = useState([]);
  const [cargando,     setCarg]         = useState(true);
  const [error,        setError]        = useState(null);
  const [detalle,      setDetalle]      = useState(null);
  const [lb,           setLb]           = useState(null);
  const [vistaPlana,   setVistaPlana]   = useState(false);
  const [chatArticulo, setChatArticulo] = useState(null);
  const [igCopied,     setIgCopied]     = useState(false);
  const [fotoTrastero, setFotoTrastero] = useState(null);
  const [userNombre,   setUserNombre]   = useState('');
  const [busqueda,     setBusqueda]     = useState('');

  const handleCopyIg = (url) => {
    navigator.clipboard.writeText(url).catch(() => {});
    setIgCopied(true);
    setTimeout(() => setIgCopied(false), 2000);
  };

  const artsFiltrados = busqueda.trim()
    ? arts.filter(a =>
        a.Nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
        a.Descripcion?.toLowerCase().includes(busqueda.toLowerCase())
      )
    : arts;

  const acc    = theme.accent     || '#667eea';
  const bg     = theme.modalBg    || '#1a1a1a';
  const pageBg = theme.background || '#000';
  const txt    = getContrast(pageBg);

  // #fetch-datos
  useEffect(() => {
    setCarg(true);
    fetch(`${API_URL}/api/trasteros/publico/${encodeURIComponent(nombre)}`)
      .then(r => r.ok ? r.json() : Promise.reject('Error'))
      .then(data => {
        const articulos = data.articulos || data;
        setArts(articulos);
        setFotoTrastero(buildBackendImgUrl(data.foto_trastero) || null);
        setUserNombre(data.user_nombre || '');
        setLogo(buildBackendImgUrl(data.foto_trastero) || null);
        setBadge(`📦 ${articulos.length} ${articulos.length === 1 ? 'artículo' : 'artículos'}`);
        setCarg(false);
      })
      .catch(() => { setError('No se pudo cargar el trastero'); setCarg(false); });
    return () => { setBadge(null); setLogo(null); };
  }, [nombre, setBadge, setLogo]);

  // Bloquear scroll del body cuando hay modal o lightbox abierto
  useEffect(() => {
    const hayModal = !!detalle || !!lb || !!chatArticulo;
    document.body.style.overflow = hayModal ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [detalle, lb, chatArticulo]);

  useEffect(() => {
    if (!lb) return;
    const handler = (e) => {
      if (e.key === 'Escape')     setLb(null);
      if (e.key === 'ArrowRight') setLb(p => p.idx < p.imgs.length - 1 ? { ...p, idx: p.idx + 1 } : p);
      if (e.key === 'ArrowLeft')  setLb(p => p.idx > 0 ? { ...p, idx: p.idx - 1 } : p);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lb]);

  const openLb = (task, i) => {
    const imgs = [task.Imagen1, task.Imagen2, task.Imagen3, task.Imagen4]
      .filter(Boolean).map(n => `${buildBackendImgUrl(task.Ruta)}/${n}`);
    setLb({ imgs, idx: i });
  };

  // #jsx-render — header, toggle, grid/flatgrid, modal detalle, lightbox
  return (
    <PageWrapper>
      <Helmet>
        <title>{nombre ? `${nombre} · Trastero` : 'Trastero'}</title>
        <meta name="description" content={`Explora los artículos de segunda mano del trastero ${nombre} en Trastero.`} />
      </Helmet>

      {/* ── Modal Chat ── */}
      {chatArticulo && (
        <Suspense fallback={null}>
          <ModalChat articulo={chatArticulo} user={user} theme={theme} onClose={() => setChatArticulo(null)} />
        </Suspense>
      )}

      {/* ── Lightbox ── */}
      {lb && (
        <LbOverlay onClick={() => setLb(null)}>
          <LbClose onClick={() => setLb(null)}>✕</LbClose>
          <LbNav prev disabled={lb.idx === 0}
            onClick={e => { e.stopPropagation(); setLb(p => ({ ...p, idx: p.idx - 1 })); }}>‹</LbNav>
          <LbImg src={lb.imgs[lb.idx]} alt="" onClick={e => e.stopPropagation()} />
          <LbNav disabled={lb.idx === lb.imgs.length - 1}
            onClick={e => { e.stopPropagation(); setLb(p => ({ ...p, idx: p.idx + 1 })); }}>›</LbNav>
          {lb.imgs.length > 1 && (
            <LbDots onClick={e => e.stopPropagation()}>
              {lb.imgs.map((_, i) => (
                <LbDot key={i} active={i === lb.idx} onClick={() => setLb(p => ({ ...p, idx: i }))} />
              ))}
            </LbDots>
          )}
        </LbOverlay>
      )}

      {/* ── Modal detalle ── */}
      {detalle && (
        <Overlay onClick={() => setDetalle(null)}>
          <DetailBox bg={bg} onClick={e => e.stopPropagation()}>
            <DetailClose bg={bg} onClick={() => setDetalle(null)}>✕</DetailClose>
            <DetailScroll>

            {/* 1. TÍTULO */}
            <DetailTitle color={getContrast(bg)}>{detalle.Nombre}</DetailTitle>

            {/* 2. FOTOS */}
            <DetailGrid
              ruta={buildBackendImgUrl(detalle.Ruta)}
              imgs={[detalle.Imagen1, detalle.Imagen2, detalle.Imagen3, detalle.Imagen4]}
              onImgClick={i => openLb(detalle, i)}
              size="large"
            />

            {/* 3. PRECIO */}
            {detalle.Precio !== null && detalle.Precio !== undefined && (
              <DetailPrice accent={acc}>
                {detalle.Precio.toLocaleString('es-ES', { minimumFractionDigits: 0 })} €
              </DetailPrice>
            )}

            {/* 3. DESCRIPCIÓN */}
            {detalle.Descripcion && (
              <DetailDesc color={getContrast(bg)}>{detalle.Descripcion}</DetailDesc>
            )}

            {/* 4. BADGES — al pie */}
            {(detalle.Categoria || detalle.Subcategoria || detalle.Negociable || detalle.AceptaCambio || formatExtra(detalle.Extras)) && (
              <DetailMeta>
                {detalle.Subcategoria && <MetaTag accent={acc}>{detalle.Subcategoria}</MetaTag>}
                {!detalle.Subcategoria && detalle.Categoria && <MetaTag accent={acc}>{detalle.Categoria}</MetaTag>}
                {formatExtra(detalle.Extras) && <MetaTag accent={acc}>{formatExtra(detalle.Extras)}</MetaTag>}
                {detalle.Negociable   && <MetaTag accent={acc}>Negociable</MetaTag>}
                {detalle.AceptaCambio && <MetaTag accent={acc}>Acepta cambio</MetaTag>}
              </DetailMeta>
            )}

            {/* 5. COMPARTIR */}
            {(() => {
              const pageUrl = window.location.href;
              const url   = encodeURIComponent(pageUrl);
              const texto = encodeURIComponent(`${detalle.Nombre}${detalle.Precio ? ` — ${detalle.Precio} €` : ''} | trasteroweb.es`);
              return (
                <ShareRow>
                  <ShareBtn as="button" onClick={() => handleCopyIg(pageUrl)} accent={acc} data-tooltip={igCopied ? '¡Enlace copiado!' : 'Copiar enlace para Instagram'} style={{ cursor: 'pointer', border: 'none' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                  </ShareBtn>
                  <ShareBtn href={`https://twitter.com/intent/tweet?text=${texto}&url=${url}`} target="_blank" rel="noopener noreferrer" accent={acc} data-tooltip="Compartir en X">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.912-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  </ShareBtn>
                  <ShareBtn href={`https://wa.me/?text=${texto}%20${url}`} target="_blank" rel="noopener noreferrer" accent={acc} data-tooltip="Compartir en WhatsApp">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.105.549 4.082 1.504 5.798L.057 23.998l6.337-1.423A11.937 11.937 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.804 9.804 0 01-4.99-1.366l-.358-.213-3.76.845.9-3.671-.233-.377A9.818 9.818 0 012.182 12C2.182 6.58 6.58 2.182 12 2.182S21.818 6.58 21.818 12 17.42 21.818 12 21.818z"/></svg>
                  </ShareBtn>
                  <ShareBtn href={`https://www.facebook.com/sharer/sharer.php?u=${url}`} target="_blank" rel="noopener noreferrer" accent={acc} data-tooltip="Compartir en Facebook">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </ShareBtn>
                </ShareRow>
              );
            })()}
            </DetailScroll>
          </DetailBox>
        </Overlay>
      )}

      {/* ── Buscador ── */}
      {!cargando && arts.length > 0 && (
        <BuscadorTop as="div">
          <ContainerBuscador>
            <VistaBtn
              type="button"
              active={vistaPlana}
              onClick={() => setVistaPlana(v => !v)}
              title={vistaPlana ? 'Ver en grupo' : 'Ver todas las imágenes'}
            >
              {vistaPlana ? '⊞' : '▦'}
            </VistaBtn>
            <FiltroSep />
            <SearchBox
              type="text"
              placeholder={`Buscar en ${nombre}...`}
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
            />
            <SearchButton type="button" onClick={() => setBusqueda('')}>
              {busqueda ? '✕' : '🔍'}
            </SearchButton>
          </ContainerBuscador>
        </BuscadorTop>
      )}

      {/* ── Contenido ── */}
      {cargando ? (
        <EmptyState>
          <div style={{ fontSize: 48 }}>⏳</div>
          <p style={{ color: txt, margin: 0 }}>Cargando...</p>
        </EmptyState>
      ) : error ? (
        <EmptyState>
          <div style={{ fontSize: 48 }}>❌</div>
          <p style={{ color: txt, margin: 0 }}>{error}</p>
        </EmptyState>
      ) : arts.length === 0 ? (
        <EmptyState>
          <div style={{ fontSize: 64 }}>📦</div>
          <p style={{ color: txt, fontSize: 18, fontWeight: 700, margin: 0 }}>Este trastero está vacío</p>
          <p style={{ color: txt + '88', margin: 0 }}>Aún no hay artículos publicados</p>
        </EmptyState>
      ) : artsFiltrados.length === 0 ? (
        <EmptyState>
          <div style={{ fontSize: 48 }}>🔍</div>
          <p style={{ color: txt, margin: 0 }}>Sin resultados para "{busqueda}"</p>
        </EmptyState>
      ) : vistaPlana ? (
        <FlatGrid>
          {artsFiltrados.flatMap(task =>
            [task.Imagen1, task.Imagen2, task.Imagen3, task.Imagen4]
              .filter(Boolean)
              .map((img, i) => {
                const allImgs = [task.Imagen1, task.Imagen2, task.Imagen3, task.Imagen4]
                  .filter(Boolean).map(n => `${buildBackendImgUrl(task.Ruta)}/${n}`);
                return (
                  <FlatCard key={`${task.id}-${i}`} bg={bg}
                    onClick={() => setLb({ imgs: allImgs, idx: i })}>
                    <FlatImg src={`${buildBackendImgUrl(task.Ruta)}/${img}`} alt={task.Nombre} loading="lazy" />
                    <CardInfo>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <CardNombre color={getContrast(bg)} style={{ flex: 1 }}>{task.Nombre}</CardNombre>
                        <ChatIconBtn style={{ color: acc }}
                          onClick={e => { e.stopPropagation(); setChatArticulo(task); }}>💬</ChatIconBtn>
                      </div>
                      {task.Precio !== null && task.Precio !== undefined && (
                        <CardPrice accent={acc}>
                          {task.Precio.toLocaleString('es-ES', { minimumFractionDigits: 0 })} €
                        </CardPrice>
                      )}
                      {task.Descripcion && <CardDesc color={getContrast(bg)}>{task.Descripcion}</CardDesc>}
                      {formatExtra(task.Extras) && (
                        <div style={{ fontSize: 12, color: getContrast(bg), opacity: 0.75 }}>
                          {formatExtra(task.Extras)}
                        </div>
                      )}
                      {(task.AceptaCambio || task.Negociable) && (
                        <CardBadges>
                          {task.AceptaCambio && <CardBadge accent={acc}>Acepta cambio</CardBadge>}
                          {task.Negociable   && <CardBadge accent={acc}>Negociable</CardBadge>}
                        </CardBadges>
                      )}
                    </CardInfo>
                  </FlatCard>
                );
              })
          )}
        </FlatGrid>
      ) : (
        <Grid>
          {artsFiltrados.map(task => (
            <TrasteroCard
              key={task.id}
              task={task}
              theme={theme}
              onOpen={t => {
                if (window.innerWidth <= 768) {
                  const allImgs = [t.Imagen1, t.Imagen2, t.Imagen3, t.Imagen4]
                    .filter(Boolean)
                    .map(n => `${buildBackendImgUrl(t.Ruta)}/${n}`);
                  setLb({ imgs: allImgs, idx: 0 });
                } else {
                  setDetalle(t);
                }
              }}
              onChat={t => setChatArticulo(t)}
            />
          ))}
        </Grid>
      )}

    </PageWrapper>
  );
}